import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Outlet } from 'react-router-dom';

import { useGetBeneficiaryVirtualAccountsQuery } from '@/api/v1';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import TransferIcon from '@/assets/icons/send-icon.svg?react';
import WithdrawIcon from '@/assets/icons/withdraw-icon.svg?react';
import { DisableInProduction } from '@/components/atoms/DisableInProduction';
import { HomeNavCard } from '@/components/molecules/HomeNavCard';
import { BalanceLayout } from '@/components/organisms/BalanceLayout';
import { MainScreen } from '@/components/organisms/MainScreen';
import { QuoteWidget } from '@/components/organisms/QuoteWidget';
import { FF_DASHBOARD_INTEGRATOR_USD_BALANCE } from '@/data/featureFlags';
import { useBalance } from '@/hooks/useBalance';
import { useCustomer } from '@/hooks/useCustomer';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { CreateUsdAccountModal } from '@/Screens/Dashboard/Customers/CreateUsdAccountModal';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { PaymentFlowType } from '@/types/paymentFlowType';
import { Permission } from '@/types/permissions';

export const Home: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const customer = useCustomer();
  const { userIsAuthorizedTo } = usePermissionsManager();

  const [isCreateUsdAccountModalOpen, setIsCreateUsdAccountModalOpen] = React.useState(false);
  const { refetch: refetchVirtualAccounts } = useGetBeneficiaryVirtualAccountsQuery(
    customer.beneficiaryId ?? '',
    { skip: !customer.beneficiaryId },
  );

  const toggleCreateUsdAccountModal = React.useCallback((): void => {
    setIsCreateUsdAccountModalOpen((prev: boolean): boolean => !prev);
  }, []);

  const isDashboardIntegratorUsdBalanceEnabled = useIntegratorFeatureFlag(
    FF_DASHBOARD_INTEGRATOR_USD_BALANCE,
  );

  const usdBalance = useBalance(CurrencyCodeType.USD);
  const usdcBalance = useBalance(CurrencyCodeType.USDC);
  const usdtBalance = useBalance(CurrencyCodeType.USDT);
  const balances = React.useMemo(
    (): DetailedBalance[] => [
      ...(isDashboardIntegratorUsdBalanceEnabled ? [usdBalance] : []),
      usdcBalance,
      usdtBalance,
    ],
    [isDashboardIntegratorUsdBalanceEnabled, usdBalance, usdcBalance, usdtBalance],
  );

  const screenTitle = React.useMemo((): string => {
    if (customer.firstName === '') {
      return tr('Welcome back');
    } else {
      return tr('WELCOME_TITLE', customer.firstName);
    }
  }, [customer.firstName, tr]);

  return (
    <MainScreen title={screenTitle}>
      <Box display="flex" flex={1} minHeight={0} width="100%" height="auto" gap={3} pt="22px">
        <Box sx={styles.balanceWidget}>
          <Paper sx={styles.balancePaper} elevation={1}>
            <BalanceLayout
              balances={balances}
              beneficiaryId={customer.beneficiaryId ?? undefined}
              onCreateUsdAccountClick={toggleCreateUsdAccountModal}
            />
          </Paper>
          {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
            <Box sx={styles.cards}>
              <HomeNavCard
                title={tr('TRANSFER')}
                link={PaymentFlowType.transfer}
                icon={TransferIcon}
              />
              <HomeNavCard
                title={tr('DEPOSIT')}
                link={PaymentFlowType.deposit}
                icon={DepositIcon}
              />
              <HomeNavCard
                title={tr('WITHDRAW')}
                link={PaymentFlowType.withdraw}
                icon={WithdrawIcon}
              />
            </Box>
          )}
        </Box>
        {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
          <DisableInProduction>
            <Paper sx={[styles.widget, styles.quoteWidget]} elevation={1}>
              <QuoteWidget />
            </Paper>
          </DisableInProduction>
        )}
      </Box>

      {customer.beneficiaryId && (
        <CreateUsdAccountModal
          open={isCreateUsdAccountModalOpen}
          beneficiaryId={customer.beneficiaryId}
          onClose={toggleCreateUsdAccountModal}
          onSuccess={refetchVirtualAccounts}
        />
      )}

      <Outlet />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  cards: {
    display: 'flex',
    mt: 2,
    gap: 3,
    alignItems: 'center',
    width: '100%',
  },
  balanceWidget: {
    flexBasis: 650,
    flexShrink: 0,
  },
  balancePaper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'background.paper',
  },
  quoteWidget: {
    flexBasis: 350,
    flexShrink: 0,
    maxHeight: 'auto',
    display: {
      sm: 'none',
      md: 'block',
    },
  },
  widget: {
    height: 'min-content',
    backgroundColor: 'background.paper',
  },
};
